import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Printer, Download, Accessibility } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { BackButton } from "@/components/BackButton";
import { PIANO_VIDEOS } from "@/data/videos";
import { VIDEO_CAPTIONS } from "@/data/videoCaptions";
import { useA11y } from "@/context/A11yContext";

const parseVtt = (txt) => {
  const out = [];
  txt.split(/\r?\n\r?\n/).forEach((block) => {
    const lines = block.split(/\r?\n/).filter((l) => l && !l.includes("-->") && l !== "WEBVTT" && !l.startsWith("NOTE") && !/^\d+$/.test(l));
    lines.forEach((l) => { const t = l.replace(/<[^>]+>/g, "").trim(); if (t && t !== out[out.length - 1]) out.push(t); });
  });
  return out;
};

export default function LyricsSheet() {
  const { id } = useParams();
  const { lyrics } = useA11y();
  const [lines, setLines] = useState(null);
  const video = PIANO_VIDEOS.find((v) => v.id === id);
  useEffect(() => {
    if (!VIDEO_CAPTIONS[id]) { setLines([]); return; }
    fetch(`/media/captions/${id}.vtt`).then((r) => r.text()).then((t) => setLines(parseVtt(t))).catch(() => setLines([]));
  }, [id]);
  const download = () => {
    const blob = new Blob([`${video?.title || id} — Jason Thompson (The JT Hub)\n\n${lines.join("\n")}\n\nLyrics auto-generated; may contain errors.\n`], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${id}-lyrics.txt`;
    a.click();
  };
  return (
    <main id="main-content" className="mx-auto max-w-2xl px-6 py-10" data-testid="lyrics-sheet">
      <div className="print:hidden"><BackButton testId="lyrics-back-button" fallback="/courses?instrument=pianists&view=videos" /></div>
      <Logo height={44} className="mt-4" />
      <p className="mt-6 font-mono text-[11px] uppercase tracking-widest text-primary">Lyrics sheet</p>
      <h1 className="mt-1 font-serif text-4xl" data-testid="lyrics-title">{video?.title || "Performance"}</h1>
      <p className="mt-1 text-sm text-muted-foreground">Live performance by Jason Thompson · lyrics auto-generated and may contain errors</p>
      {!lyrics ? (
        <div className="mt-8 rounded-2xl border border-dashed border-border bg-card p-6 text-sm text-muted-foreground" data-testid="lyrics-locked">
          Lyrics are shown when "Song lyrics on performance videos" is on in Accessibility.
          <Button size="sm" className="mt-3 flex rounded-full" onClick={() => window.dispatchEvent(new Event("jt-open-a11y"))} data-testid="lyrics-open-a11y"><Accessibility size={14} className="mr-1.5" /> Open Accessibility</Button>
        </div>
      ) : lines === null ? <p className="mt-8 text-sm text-muted-foreground">Loading…</p> : lines.length === 0 ? (
        <p className="mt-8 text-sm text-muted-foreground" data-testid="lyrics-empty">No lyrics for this performance — it's instrumental.</p>
      ) : (
        <>
          <div className="mt-5 flex gap-2 print:hidden">
            <Button size="sm" className="rounded-full" onClick={() => window.print()} data-testid="lyrics-print"><Printer size={14} className="mr-1.5" /> Print</Button>
            <Button size="sm" variant="outline" className="rounded-full" onClick={download} data-testid="lyrics-download"><Download size={14} className="mr-1.5" /> Download</Button>
          </div>
          <div className="mt-8 space-y-1.5 font-serif text-lg leading-relaxed" data-testid="lyrics-lines">
            {lines.map((l, i) => <p key={i}>{l}</p>)}
          </div>
        </>
      )}
    </main>
  );
}
